import { Component, inject, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ModelsUser } from "../../../app/api/model/models";
import { ApiUserRequest } from "../../../app/api/model/apiUserRequest";
import { ApiUserDataResponse } from "../../../app/api/model/apiUserDataResponse";
import { ApiUserListDataResponse } from "../../../app/api/model/apiUserListDataResponse";
import { UsersService } from "../../../app/api/api/users.service";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { ButtonModule } from "primeng/button";
import { Table, TableModule } from "primeng/table";
import { PaginatorModule } from "primeng/paginator";
import { RippleModule } from "primeng/ripple";
import { ToolbarModule } from "primeng/toolbar";
import { InputTextModule } from "primeng/inputtext";
import { ConfirmationService, MessageService } from "primeng/api";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";
import { HrEmployeesForm } from './hr.employees.form';
import { Card } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { CompanyService } from '../../../app/api/api/company.service';
import { ModelsCompanyPosition } from '../../../app/api/model/models';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
  selector: 'app-hr-employees',
  template: `
    <p-toast></p-toast>
    <p-confirmDialog [style]="{width: '450px'}"></p-confirmDialog>
    <app-hr-employee-form 
      [visible]="employeeDialog" 
      [employee]="employee" 
      [positions]="availablePositions"
      [canEditRole]="canEditRole"
      (onSave)="saveEmployee($event)"
      (onCancel)="hideDialog()"></app-hr-employee-form>


    <p-card>
      <div class="flex flex-row justify-between items-center mb-4 gap-4">
        <div class="flex items-center gap-4">
          @if (ENABLE_ADD) {
            <p-button label="Create" severity="success" icon="pi pi-plus" (click)="openNew()"/>
          }
          <div class="flex items-center gap-2">
            <p-toggleSwitch 
              id="archived_toggle" 
              [(ngModel)]="showArchived" 
              (onChange)="onArchivedToggleChange()"
            />
            <label for="archived_toggle" class="font-semibold text-zinc-700 dark:text-zinc-300 select-none cursor-pointer text-lg">
              {{ 'Archived' | transloco }}
            </label>
          </div>
        </div>
        <div></div>
      </div>
      <p-table
        #dt
        [value]="employees"
        [rowHover]="true"
        dataKey="id"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 20, 50]"
        [lazy]="true"
        [totalRecords]="totalRecords"
        [loading]="loading"
        (onLazyLoad)="loadEmployees($event)"
        [customSort]="true"
        (sortFunction)="customSort($event)"
        tableStyleClass="mb-4">
        <ng-template pTemplate="header">
          <tr class="hidden md:table-row">
            <th pSortableColumn="first_name">{{ 'First Name' | transloco }}
              <p-sortIcon field="first_name"></p-sortIcon>
            </th>
            <th pSortableColumn="last_name">{{ 'Last Name' | transloco }}
              <p-sortIcon field="last_name"></p-sortIcon>
            </th>
            <th pSortableColumn="phone">{{ 'Phone' | transloco }}
              <p-sortIcon field="phone"></p-sortIcon>
            </th>
            <th pSortableColumn="wa_opt_in">{{ 'WA' | transloco }}
              <p-sortIcon field="wa_opt_in"></p-sortIcon>
            </th>
            <th>{{ 'Activity' | transloco }}</th>
            <th>
              {{ 'Position' | transloco }}
            </th>
           <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-employee>
          <!-- Desktop Row -->
          <tr class="hidden md:table-row">
            <td>{{ employee.first_name }}</td>
            <td>{{ employee.last_name }}</td>
            <td>{{ employee.phone }}</td>
            <td>
              @if (employee.wa_opt_in) {
                <i class="pi pi-whatsapp text-green-500 text-lg"></i>
              } @else {
                <i class="pi pi-whatsapp text-red-500 text-lg"></i>
              }
            </td>
            <td>
              <div class="flex items-start gap-2">
                <span [class]="getActivityDetails(employee.activity).dotClass" style="width: 10px; height: 10px; border-radius: 50%; display: inline-block; margin-top: 6px;"></span>
                <div class="flex flex-col">
                  <span class="font-bold" [class]="getActivityDetails(employee.activity).colorClass">
                    {{ getActivityDetails(employee.activity).label }}
                  </span>
                  <span class="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    {{ getActivityDetails(employee.activity).subtext }}
                  </span>
                </div>
              </div>
            </td>
            <td>
              <div class="flex flex-wrap gap-1">
                @for (pos of employee.positions; track pos.id) {
                  <p-tag [value]="pos.name" severity="secondary"></p-tag>
                }
              </div>
            </td>
           <td class="w-0">
             <div class="flex flex-row flex-nowrap gap-2">
               <button 
                 pButton 
                 pRipple 
                 icon="pi pi-pencil" 
                 class="p-button-rounded p-button-success"
                 (click)="editEmployee(employee)">
               </button>
               @if (!showArchived) {
                 <button 
                   pButton 
                   pRipple 
                   icon="pi pi-trash" 
                   class="p-button-rounded p-button-danger"
                   (click)="archiveEmployee(employee)">
                 </button>
               } @else {
                 <button 
                   pButton 
                   pRipple 
                   icon="pi pi-undo" 
                   class="p-button-rounded p-button-secondary"
                   (click)="unarchiveEmployee(employee)">
                 </button>
               }
             </div>
           </td>
          </tr>
          <!-- Mobile Card -->
          <tr class="md:hidden">
            <td colspan="7" style="padding: 0">
              <div class="border rounded-xl p-4 shadow-sm bg-white dark:bg-zinc-900 my-2 mx-1" style="border-color: #2d8a4e;">
                <div class="flex justify-between items-start mb-3">
                  <div>
                    <div class="text-xl font-bold">{{ employee.first_name }} {{ employee.last_name }}</div>
                    <div class="text-zinc-500 flex items-center gap-2 mt-1">
                      <i class="pi pi-phone text-sm"></i>
                      {{ employee.phone }}
                      @if (employee.wa_opt_in) {
                        <i class="pi pi-whatsapp text-green-500"></i>
                      } @else {
                        <i class="pi pi-whatsapp text-red-500"></i>
                      }
                    </div>
                  </div>
                  <div class="flex gap-1">
                    <button 
                      pButton 
                      pRipple 
                      icon="pi pi-pencil" 
                      class="p-button-rounded p-button-text p-button-success"
                      (click)="editEmployee(employee)">
                    </button>
                    @if (!showArchived) {
                      <button 
                        pButton 
                        pRipple 
                        icon="pi pi-trash" 
                        class="p-button-rounded p-button-text p-button-danger"
                        (click)="archiveEmployee(employee)">
                      </button>
                    } @else {
                      <button 
                        pButton 
                        pRipple 
                        icon="pi pi-undo" 
                        class="p-button-rounded p-button-text p-button-secondary"
                        (click)="unarchiveEmployee(employee)">
                      </button>
                    }
                  </div>
                </div>
                
                <div class="flex flex-wrap items-center gap-2">
                  <p-tag [value]="employee.role | transloco" [severity]="getRoleSeverity(employee.role)"></p-tag>
                  @if (employee.positions && employee.positions.length > 0) {
                    <span class="text-zinc-300">|</span>
                    <div class="flex flex-wrap gap-1">
                      @for (pos of employee.positions; track pos.id) {
                        <p-tag [value]="pos.name" severity="secondary" [rounded]="true"></p-tag>
                      }
                    </div>
                  }
                </div>
                
                <div class="flex items-center gap-2 mt-2 pt-2 border-t border-zinc-100 dark:border-zinc-800 text-xs">
                  <span [class]="getActivityDetails(employee.activity).dotClass" style="width: 8px; height: 8px; border-radius: 50%; display: inline-block;"></span>
                  <span class="font-bold" [class]="getActivityDetails(employee.activity).colorClass">
                    {{ getActivityDetails(employee.activity).label }}
                  </span>
                  <span class="text-zinc-500">({{ getActivityDetails(employee.activity).subtext }})</span>
                </div>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>
  `,
  standalone: true,
  imports: [
    FormsModule,
    ToggleSwitchModule,
    ButtonModule,
    TableModule,
    PaginatorModule,
    RippleModule,
    ToolbarModule,
    InputTextModule,
    HrEmployeesForm,
    ConfirmDialogModule,
    ToastModule,
    Card,
    TagModule,
    TranslocoModule,
    SelectModule
  ],
  providers: [MessageService, ConfirmationService]
})
export class HrEmployeesIndex implements OnInit, AfterViewInit {
  @ViewChild('dt') dt!: Table;

  private usersApi = inject(UsersService);
  private companyApi = inject(CompanyService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private transloco = inject(TranslocoService);

  readonly ENABLE_ADD = false;
  showArchived = false;

  currentUser?: ModelsUser;
  employees: ModelsUser[] = [];
  availablePositions: ModelsCompanyPosition[] = [];
  employeeDialog = false;
  employee: ModelsUser = {} as ModelsUser;
  totalRecords = 0;
  loading = false;
  private lastLazyLoadEvent?: any;

  get canEditRole(): boolean {
    return this.currentUser?.role === 'owner';
  }

  ngOnInit() {
    this.usersApi.apiUserGet().subscribe((r: ApiUserDataResponse) => {
      if (r.data) {
        this.currentUser = r.data;
        this.loadCompanyPositions();
      }
    });
  }

  ngAfterViewInit() {
    if (this.dt) {
      this.dt.sort = (event: any) => this.customSort(event);
    }
  }

  customSort(event: any) {
    const field = event.field;
    if (this.dt.sortField === field) {
      if (this.dt.sortOrder === 1) {
        this.dt.sortOrder = -1;
        this.dt.sortSingle();
      } else if (this.dt.sortOrder === -1) {
        this.dt.sortField = null;
        this.dt.sortOrder = 1;
        this.dt.tableService.onSort(null);
        if (this.dt.lazy) {
          this.dt.onLazyLoad.emit(this.dt.createLazyLoadMetadata());
        }
      }
    } else {
      this.dt.sortField = field;
      this.dt.sortOrder = 1;
      this.dt.sortSingle();
    }
    if (this.dt.resetPageOnSort) {
      this.dt.first = 0;
      this.dt.firstChange.emit(0);
      if (this.dt.scrollable) {
        this.dt.resetScrollTop();
      }
    }
    if (this.dt.isStateful()) {
      this.dt.saveState();
    }
    this.dt.anchorRowIndex = null;
  }

  loadCompanyPositions() {
    this.companyApi.apiCompanyPositionsGet('true').subscribe(r => {
      if (r.data) {
        this.availablePositions = r.data.map(p => ({
          ...p,
          name: p.name || p.position?.name
        }));
      }
    });
  }

  loadEmployees(event?: any) {
    if (event) {
      this.lastLazyLoadEvent = event;
    }
    this.loading = true;
    const page = this.lastLazyLoadEvent ? Math.floor(this.lastLazyLoadEvent.first / this.lastLazyLoadEvent.rows) + 1 : 1;
    const pageSize = this.lastLazyLoadEvent ? this.lastLazyLoadEvent.rows : 10;

    let sortParam: string | undefined = undefined;
    if (this.lastLazyLoadEvent?.sortField) {
      const orderStr = this.lastLazyLoadEvent.sortOrder === -1 ? 'desc' : 'asc';
      sortParam = `${this.lastLazyLoadEvent.sortField}:${orderStr}`;
    }

    this.usersApi.apiUsersGet(
      undefined,
      this.showArchived,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      sortParam,
      page,
      pageSize).subscribe({
        next: (r: ApiUserListDataResponse) => {
          if (r.data) {
            this.employees = r.data;
          }
          if (r.total !== undefined) {
            this.totalRecords = r.total;
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  openNew() {
    this.employee = {} as ModelsUser;
    this.employeeDialog = true;
  }

  hideDialog() {
    this.employeeDialog = false;
  }

  saveEmployee(employee: ModelsUser) {
    const isNew = !employee.id;
    const selectedPositionIds = employee.positions?.map(p => p.id!) || [];
    const roles: string[] = [];

    selectedPositionIds.forEach(id => {
      const matchedPos = this.availablePositions.find(ap => ap.id === id);
      const role = matchedPos?.position?.role;
      if (role) {
        role.split(',').forEach(r => {
          const cleanRole = r.trim().toLowerCase();
          if (cleanRole && !roles.includes(cleanRole)) {
            roles.push(cleanRole);
          }
        });
      }
    });

    const payload: ApiUserRequest = {
      company_id: employee.company_id || this.currentUser?.company_id || 1,
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      phone: employee.phone || '',
      role: roles.length > 0 ? roles.join(',') : (employee.role || 'employee'),
      language_id: employee.language_id || this.currentUser?.language_id || 1,
      wa_opt_in: employee.wa_opt_in || false,
      position_ids: selectedPositionIds
    };

    if (isNew) {
      this.usersApi.apiUsersPost(payload).subscribe({
        next: () => {
          this.loadEmployees();
          this.employeeDialog = false;
          this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Employee Created') as string, life: 3000 });
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: this.transloco.translate('Error') as string, detail: this.transloco.translate('Failed to create employee') as string, life: 3000 });
        }
      });
    } else {
      this.usersApi.apiUsersIdPut(employee.id!, payload).subscribe({
        next: () => {
          this.loadEmployees();
          this.employeeDialog = false;
          this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Employee Updated') as string, life: 3000 });
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: this.transloco.translate('Error') as string, detail: this.transloco.translate('Failed to update employee') as string, life: 3000 });
        }
      });
    }
  }

  onArchivedToggleChange() {
    if (this.lastLazyLoadEvent) {
      this.lastLazyLoadEvent.first = 0;
    }
    if (this.dt) {
      this.dt.first = 0;
    }
    this.loadEmployees();
  }

  archiveEmployee(employee: ModelsUser) {
    this.confirmationService.confirm({
      message: (this.transloco.translate('Are you sure you want to archive') as string) + ' ' + employee.first_name + ' ' + employee.last_name + '?',
      header: this.transloco.translate('Confirm') as string,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersApi.apiUsersIdDelete(employee.id!).subscribe({
          next: () => {
            this.loadEmployees();
            this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Employee Archived') as string, life: 3000 });
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: this.transloco.translate('Error') as string, detail: this.transloco.translate('Failed to archive employee') as string, life: 3000 });
          }
        });
      }
    });
  }

  unarchiveEmployee(employee: ModelsUser) {
    this.confirmationService.confirm({
      message: (this.transloco.translate('Are you sure you want to unarchive') as string) + ' ' + employee.first_name + ' ' + employee.last_name + '?',
      header: this.transloco.translate('Confirm') as string,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersApi.apiUsersIdUnarchivePost(employee.id!).subscribe({
          next: () => {
            this.loadEmployees();
            this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Employee Unarchived') as string, life: 3000 });
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: this.transloco.translate('Error') as string, detail: this.transloco.translate('Failed to unarchive employee') as string, life: 3000 });
          }
        });
      }
    });
  }

  editEmployee(employee: ModelsUser) {
    this.employee = { ...employee };
    this.employeeDialog = true;
  }

  getRoleSeverity(role: string | undefined): "success" | "info" | "warn" | "danger" | "secondary" | "contrast" | undefined {
    switch (role?.toLowerCase()) {
      case 'owner':
        return 'success';
      case 'manager':
        return 'info';
      case 'employee':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  getActivityDetails(activity: any) {
    if (!activity || !activity.status) {
      return {
        label: this.transloco.translate('None'),
        colorClass: 'text-red-500 dark:text-red-400',
        dotClass: 'bg-red-500',
        subtext: this.transloco.translate('No activity')
      };
    }

    const status = activity.status.toLowerCase();
    const days = activity.days_active || 0;

    if (status === 'active') {
      return {
        label: this.transloco.translate('Active'),
        colorClass: 'text-green-600 dark:text-green-500',
        dotClass: 'bg-green-500',
        subtext: days === 1
          ? this.transloco.translate('1 day active')
          : this.transloco.translate('days active', { count: days })
      };
    } else if (status === 'low') {
      return {
        label: this.transloco.translate('Low'),
        colorClass: 'text-orange-500 dark:text-orange-400',
        dotClass: 'bg-orange-500',
        subtext: days === 1
          ? this.transloco.translate('1 day active')
          : this.transloco.translate('days active', { count: days })
      };
    } else {
      return {
        label: this.transloco.translate('None'),
        colorClass: 'text-red-500 dark:text-red-400',
        dotClass: 'bg-red-500',
        subtext: this.transloco.translate('No activity')
      };
    }
  }
}
